// @ts-nocheck
import React, { Suspense, ReactElement } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

import { PrivateRoute } from "@/components/PrivateRoute";
import LoginPage from "@/containers/Auth/LoginPage";
import { URLS } from "@/urls";
import { LoadingComponent } from "@/components/LoadingComponent";

// NetSG Mobile v2 (TIAB2) customer routes are always registered; the Customer
// UI hides/shows the Mobile section based on whether the company actually owns
// a v2 service (see MainNavigation), so an unentitled customer never sees a
// link to these paths.

/**
 * Auth related.
 */
const AuthCallback = React.lazy(() => import("@/containers/Auth/AuthCallback"));
const LogoutPage = React.lazy(() => import("@/containers/Auth/LogoutPage"));
const AccountNotSetYetPage = React.lazy(
  () => import("@/containers/Auth/AccountNotSetYetPage"),
);

/**
 * Exceptions
 */

const PageNotFound = React.lazy(
  () => import("@/containers/Exceptions/PageNotFound"),
);

/***************************
 * SUPERADMIN APPLICATION
 ***************************/

const SuperAdminApplication = {
  /**
   * Service management.
   */
  Locations: React.lazy(() => import("@/containers/StaffUI/Locations")),
  Circuits: React.lazy(() => import("@/containers/StaffUI/Circuits")),
  Devices: React.lazy(() => import("@/containers/StaffUI/Devices")),
  MaintenanceContracts: React.lazy(
    () => import("@/containers/StaffUI/MaintenanceContracts"),
  ),
  Services: React.lazy(() => import("@/containers/StaffUI/Services")),
  TelcoInaBoxSimCards: React.lazy(
    () => import("@/containers/StaffUI/MobileTelcoInABox/SimCards"),
  ),
  NetSGMobileServices: React.lazy(
    () => import("@/containers/StaffUI/NetSGMobile/Services"),
  ),
  NetSGMobileDataPools: React.lazy(
    () => import("@/containers/StaffUI/NetSGMobile/DataPools"),
  ),
  CellularSIMCards: React.lazy(
    () => import("@/containers/StaffUI/CellularSIMCards"),
  ),
  CellularSIMCardsChangeLogs: React.lazy(
    () => import("@/containers/StaffUI/CellularSIMCardChangeLogs"),
  ),
  ManagedServices: React.lazy(
    () => import("@/containers/StaffUI/ManagedServices"),
  ),

  /**
   * Client management.
   */
  Companies: React.lazy(() => import("@/containers/StaffUI/Companies")),
  Contacts: React.lazy(() => import("@/containers/StaffUI/Contacts")),

  /**
   * Change management.
   */
  ChangeCalendar: React.lazy(
    () => import("@/containers/StaffUI/ChangeCalendar"),
  ),
  Changes: React.lazy(() => import("@/containers/StaffUI/Changes")),
  ChangeRequests: React.lazy(
    () => import("@/containers/StaffUI/ChangeRequests"),
  ),
  ChangeRequestProxyRedirect: React.lazy(
    () => import("@/containers/StaffUI/ChangeRequestProxyRedirect"),
  ),
  ChangeRequestPreview: React.lazy(
    () => import("@/containers/StaffUI/ChangeRequestPreview"),
  ),

  /**
   * Orders management.
   */
  Orders: React.lazy(() => import("@/containers/StaffUI/Orders")),

  /**
   * Reports.
   */
  InvoicesReport: React.lazy(
    () => import("@/containers/StaffUI/Reports/Invoices"),
  ),
  CircuitsStartDateDeltaReport: React.lazy(
    () => import("@/containers/StaffUI/Reports/CircuitsStartDateDelta"),
  ),
  OrdersPDDStatusReport: React.lazy(
    () => import("@/containers/StaffUI/Reports/OrdersPDDStatus"),
  ),
  DeviceFirmwareReport: React.lazy(
    () => import("@/containers/StaffUI/Reports/DeviceFirmware"),
  ),
  ManagedServiceDevicesReport: React.lazy(
    () => import("@/containers/StaffUI/Reports/ManagedServiceDevices"),
  ),
  SiteDeploymentStatusReport: React.lazy(
    () => import("@/containers/StaffUI/Reports/SiteDeploymentStatus"),
  ),

  /**
   * Voice billing.
   */
  SIPTrunks: React.lazy(
    () => import("@/containers/StaffUI/VoiceBilling/SIPTrunks"),
  ),
  PhoneNumberRanges: React.lazy(
    () => import("@/containers/StaffUI/VoiceBilling/PhoneNumberRanges"),
  ),
  AnswerPoints: React.lazy(
    () => import("@/containers/StaffUI/VoiceBilling/AnswerPoints"),
  ),

  /**
   * Admin.
   */
  Carriers: React.lazy(() => import("@/containers/StaffUI/Admin/Carriers")),
  MaintenanceOrganizations: React.lazy(
    () => import("@/containers/StaffUI/Admin/MaintenanceOrganizations"),
  ),
  ServiceCategories: React.lazy(
    () => import("@/containers/StaffUI/Admin/ServiceCategories"),
  ),
  ServiceTypes: React.lazy(
    () => import("@/containers/StaffUI/Admin/ServiceTypes"),
  ),
  RatePlans: React.lazy(() => import("@/containers/StaffUI/Admin/RatePlans")),
  MobileAddOns: React.lazy(
    () => import("@/containers/StaffUI/Admin/MobileAddOns"),
  ),
  Bandwidths: React.lazy(() => import("@/containers/StaffUI/Admin/Bandwidths")),
  CircuitTypes: React.lazy(
    () => import("@/containers/StaffUI/Admin/CircuitTypes"),
  ),
  DeviceManufacturers: React.lazy(
    () => import("@/containers/StaffUI/Admin/DeviceManufacturers"),
  ),
  DeviceTags: React.lazy(() => import("@/containers/StaffUI/Admin/DeviceTags")),
  CustomerProvisioningStatuses: React.lazy(
    () => import("@/containers/StaffUI/Admin/Provisioning/CustomerStatuses"),
  ),
  ServiceMessages: React.lazy(
    () => import("@/containers/StaffUI/Admin/ServiceMessages"),
  ),
  EmailSettings: React.lazy(
    () => import("@/containers/StaffUI/Admin/Notifications/EmailSettings"),
  ),
  Emails: React.lazy(
    () => import("@/containers/StaffUI/Admin/Notifications/Emails"),
  ),
  Users: React.lazy(() => import("@/containers/StaffUI/Admin/Users")),
  UserGroups: React.lazy(
    () => import("@/containers/StaffUI/Admin/Permissions/UserGroups"),
  ),
  Permissions: React.lazy(
    () => import("@/containers/StaffUI/Admin/Permissions"),
  ),
  PermissionsSets: React.lazy(
    () => import("@/containers/StaffUI/Admin/Permissions/PermissionsSets"),
  ),
  HandoverDocumentTemplates: React.lazy(
    () =>
      import("@/containers/StaffUI/Admin/CustomerHandover/DocumentTemplates"),
  ),
  InterfaceTypes: React.lazy(
    () => import("@/containers/StaffUI/Admin/InterfaceTypes"),
  ),
  InterfaceConfigurations: React.lazy(
    () => import("@/containers/StaffUI/Admin/InterfaceConfigurations"),
  ),
  WorkPlanTemplates: React.lazy(
    () => import("@/containers/StaffUI/Admin/WorkPlanTemplates"),
  ),
  ConnectWiseSyncEvents: React.lazy(
    () => import("@/containers/StaffUI/Admin/ConnectWise/SyncEvents"),
  ),
  NetBRMOverview: React.lazy(
    () => import("@/containers/StaffUI/Admin/NetBRM/Overview"),
  ),
  NetBRMMappings: React.lazy(
    () => import("@/containers/StaffUI/Admin/NetBRM/Mappings"),
  ),
  NetBRMSyncQueue: React.lazy(
    () => import("@/containers/StaffUI/Admin/NetBRM/SyncQueue"),
  ),
  NetBRMLinks: React.lazy(
    () => import("@/containers/StaffUI/Admin/NetBRM/Links"),
  ),
  NetBRMAccountOverrides: React.lazy(
    () => import("@/containers/StaffUI/Admin/NetBRM/AccountOverrides"),
  ),
  NetBRMRestrictionDecisions: React.lazy(
    () => import("@/containers/StaffUI/Admin/NetBRM/RestrictionDecisions"),
  ),
  NetBRMWebhookEvents: React.lazy(
    () => import("@/containers/StaffUI/Admin/NetBRM/WebhookEvents"),
  ),
  NetBRMInboundOrders: React.lazy(
    () => import("@/containers/StaffUI/Admin/NetBRM/InboundOrders"),
  ),
  NetBRMCompanyBilling: React.lazy(
    () => import("@/containers/StaffUI/Admin/NetBRM/CompanyBilling"),
  ),
  VoiceCodecs: React.lazy(
    () => import("@/containers/StaffUI/Admin/VoiceBilling/VoiceCodecs"),
  ),
  DeviceCategories: React.lazy(
    () => import("@/containers/StaffUI/Admin/DeviceCategories"),
  ),

  /**
   * Forms constructor.
   */
  FormBuilderTemplates: React.lazy(
    () =>
      import("@/containers/StaffUI/Admin/FormBuilder/ConstructedFormTemplates"),
  ),
  FormBuilderForms: React.lazy(
    () => import("@/containers/StaffUI/Admin/FormBuilder/ConstructedForms"),
  ),

  /**
   * Operations.
   */
  CircuitRelocation: React.lazy(
    () => import("@/containers/StaffUI/Admin/Operations/CircuitRelocation"),
  ),
  TagManagement: React.lazy(
    () => import("@/containers/StaffUI/Admin/Operations/TagManagement"),
  ),

  /**
   * Work plans.
   */
  WorkPlans: React.lazy(() => import("@/containers/StaffUI/WorkPlans")),

  /**
   * Dashboard and misc.
   */
  Dashboard: React.lazy(() => import("@/containers/StaffUI/Dashboard")),

  /**
   * User invitations.
   */
  AcceptUserInvitation: React.lazy(
    () => import("@/containers/Shared/AcceptUserInvitation"),
  ),
};

/********************************
 * END OF SUPERADMIN APPLICATION
 ********************************/

/********************************
 * CUSTOMER APPLICATION
 ********************************/

const CustomerApplication = {
  CompanyProfile: React.lazy(
    () => import("@/containers/CustomerUI/CompanyProfile"),
  ),
  Dashboard: React.lazy(() => import("@/containers/CustomerUI/Dashboard")),
  OrderDeliveryPage: React.lazy(
    () => import("@/containers/CustomerUI/ServiceDelivery/OrderDeliveryPage"),
  ),
  OrderDeliveryDetailsPage: React.lazy(
    () =>
      import("@/containers/CustomerUI/ServiceDelivery/OrderDeliveryDetailsPage"),
  ),
  ActiveServicesListPage: React.lazy(
    () => import("@/containers/CustomerUI/Services/ActiveServicesListPage"),
  ),
  ActiveServiceDetailsPage: React.lazy(
    () => import("@/containers/CustomerUI/Services/ActiveServiceDetailsPage"),
  ),
  TelcoInaBoxSimCardsListPage: React.lazy(
    () => import("@/containers/CustomerUI/MobileVoice/TelcoInaBoxSimCardsList"),
  ),
  TelcoInaBoxSimCardDetails: React.lazy(
    () =>
      import("@/containers/CustomerUI/MobileVoice/TelcoInaBoxSimCardDetails"),
  ),
  CustomerNetSGMobileServicesList: React.lazy(
    () => import("@/containers/CustomerUI/NetSGMobile/ServicesList"),
  ),
  CustomerNetSGMobileServiceDetail: React.lazy(
    () => import("@/containers/CustomerUI/NetSGMobile/ServiceDetail"),
  ),
  CustomerNetSGMobilePoolsList: React.lazy(
    () => import("@/containers/CustomerUI/NetSGMobile/PoolsList"),
  ),
  CustomerNetSGMobilePoolDetail: React.lazy(
    () => import("@/containers/CustomerUI/NetSGMobile/PoolDetail"),
  ),
  ManagedServicesListPage: React.lazy(
    () =>
      import("@/containers/CustomerUI/ManagedServices/ManagedServicesListPage"),
  ),
  ManagedServiceDetailsPage: React.lazy(
    () =>
      import("@/containers/CustomerUI/ManagedServices/ManagedServiceDetailsPage"),
  ),
  ManagedDeviceDetailsPage: React.lazy(
    () =>
      import("@/containers/CustomerUI/ManagedServices/ManagedDeviceDetailsPage"),
  ),
  ChangesListPage: React.lazy(
    () => import("@/containers/CustomerUI/Changes/ChangesListPage"),
  ),
  ChangeDetailsPage: React.lazy(
    () => import("@/containers/CustomerUI/Changes/ChangeDetailsPage"),
  ),
  ChangeRequestsListPage: React.lazy(
    () =>
      import("@/containers/CustomerUI/ChangeRequests/ChangeRequestsListPage"),
  ),
  ChangeRequestDetailsPage: React.lazy(
    () =>
      import("@/containers/CustomerUI/ChangeRequests/ChangeRequestDetailsPage"),
  ),
  ChangeRequestProxyRedirect: React.lazy(
    () =>
      import("@/containers/CustomerUI/ChangeRequests/ChangeRequestProxyRedirect"),
  ),
  SupportTicketsPage: React.lazy(
    () => import("@/containers/CustomerUI/Support/SupportTicketsPage"),
  ),
  OpenTicketPage: React.lazy(
    () => import("@/containers/CustomerUI/Support/OpenTicketPage"),
  ),
  TicketDetailsPage: React.lazy(
    () => import("@/containers/CustomerUI/Support/TicketDetailsPage"),
  ),
  MyNetwork: React.lazy(() => import("@/containers/CustomerUI/MyNetwork")),
  ReportsListPage: React.lazy(() => import("@/containers/CustomerUI/Reports")),
  ServicesReport: React.lazy(
    () => import("@/containers/CustomerUI/Reports/Services"),
  ),
  SupportTicketsReport: React.lazy(
    () => import("@/containers/CustomerUI/Reports/SupportTickets"),
  ),
  ChangeManagementReport: React.lazy(
    () => import("@/containers/CustomerUI/Reports/ChangeManagement"),
  ),
  ManagedDevicesReport: React.lazy(
    () => import("@/containers/CustomerUI/Reports/ManagedDevices"),
  ),
  ManagedServicesDeviceAvailabilityReport: React.lazy(
    () =>
      import("@/containers/CustomerUI/Reports/ManagedServicesDeviceAvailability"),
  ),
  ServiceAvailabilityReport: React.lazy(
    () => import("@/containers/CustomerUI/Reports/ServiceAvailability"),
  ),
  AlertHistoryReport: React.lazy(
    () => import("@/containers/CustomerUI/Reports/AlertHistory"),
  ),
  CircuitUtilizationByLocationReport: React.lazy(
    () =>
      import("@/containers/CustomerUI/Reports/CircuitUtilizationByLocation"),
  ),
  SiteDeploymentStatusReport: React.lazy(
    () => import("@/containers/CustomerUI/Reports/SiteDeploymentStatus"),
  ),

  DDoS: React.lazy(() => import("@/containers/CustomerUI/DDoS")),

  AcceptUserInvitation: React.lazy(
    () => import("@/containers/Shared/AcceptUserInvitation"),
  ),
};

const PublicApplication = {
  ConstructedFormPage: React.lazy(
    () => import("@/containers/PublicUI/FormBuilder/ConstructedFormPage"),
  ),
  ChangeRequestApproval: React.lazy(
    () => import("@/containers/PublicUI/ChangeRequestApproval"),
  ),
  AcceptUserInvitation: React.lazy(
    () => import("@/containers/PublicUI/AcceptUserInvitation"),
  ),
};

/********************************
 * END OF CUSTOMER APPLICATION
 ********************************/

interface PrivateElementProps {
  element: ReactElement;
}

const PrivateElement = ({ element }: PrivateElementProps): ReactElement => {
  return <PrivateRoute>{element}</PrivateRoute>;
};

export const superAdminRoutes = (
  <Suspense fallback={<LoadingComponent />}>
    <Routes>
      <Route
        path={URLS.auth.login.route}
        element={<Navigate to={URLS.dashboard.index.buildPath()} replace />}
      />

      <Route
        path={URLS.auth.authCallback.route}
        element={<PrivateElement element={<AuthCallback />} />}
      />

      <Route
        path={URLS.account.logout.route}
        element={<PrivateElement element={<LogoutPage />} />}
      />

      <Route
        path={URLS.dashboard.index.route}
        element={
          <PrivateElement element={<SuperAdminApplication.Dashboard />} />
        }
      />

      {/* Service Management */}
      <Route
        path={URLS.serviceManagement.locations.list.route}
        element={
          <PrivateElement element={<SuperAdminApplication.Locations />} />
        }
      />
      <Route
        path={URLS.serviceManagement.circuits.list.route}
        element={
          <PrivateElement element={<SuperAdminApplication.Circuits />} />
        }
      />
      <Route
        path={URLS.serviceManagement.devicesAndContracts.devices.list.route}
        element={<PrivateElement element={<SuperAdminApplication.Devices />} />}
      />
      <Route
        path={
          URLS.serviceManagement.devicesAndContracts.maintenanceContracts.list
            .route
        }
        element={
          <PrivateElement
            element={<SuperAdminApplication.MaintenanceContracts />}
          />
        }
      />
      <Route
        path={URLS.serviceManagement.services.list.route}
        element={
          <PrivateElement element={<SuperAdminApplication.Services />} />
        }
      />
      <Route
        path={URLS.serviceManagement.mobile.cellularSIMCards.list.route}
        element={
          <PrivateElement
            element={<SuperAdminApplication.CellularSIMCards />}
          />
        }
      />
      <Route
        path={
          URLS.serviceManagement.mobile.cellularSIMCardChangeLogs.list.route
        }
        element={
          <PrivateElement
            element={<SuperAdminApplication.CellularSIMCardsChangeLogs />}
          />
        }
      />
      <Route
        path={URLS.serviceManagement.mobileVoice.simCards.list.route}
        element={
          <PrivateElement
            element={<SuperAdminApplication.TelcoInaBoxSimCards />}
          />
        }
      />
      <Route
        path={URLS.serviceManagement.netsgMobile.services.list.route}
        element={
          <PrivateElement
            element={<SuperAdminApplication.NetSGMobileServices />}
          />
        }
      />
      <Route
        path={URLS.serviceManagement.netsgMobile.pools.list.route}
        element={
          <PrivateElement
            element={<SuperAdminApplication.NetSGMobileDataPools />}
          />
        }
      />
      {/* The standalone SIM cards screen was folded into the services screen
          (one service = one SIM); keep old bookmarks working. */}
      <Route
        path={URLS.serviceManagement.netsgMobile.simCards.list.route}
        element={
          <Navigate
            to={URLS.serviceManagement.netsgMobile.services.list.buildPath()}
            replace
          />
        }
      />
      <Route
        path={URLS.serviceManagement.managedServices.list.route}
        element={
          <PrivateElement element={<SuperAdminApplication.ManagedServices />} />
        }
      />

      {/* Client Management */}
      <Route
        path={URLS.clientManagement.companies.list.route + "/*"}
        element={
          <PrivateElement element={<SuperAdminApplication.Companies />} />
        }
      />
      <Route
        path={URLS.clientManagement.contacts.list.route}
        element={
          <PrivateElement element={<SuperAdminApplication.Contacts />} />
        }
      />

      {/* Change Management */}
      <Route
        path={URLS.changeManagement.calendar.route}
        element={
          <PrivateElement element={<SuperAdminApplication.ChangeCalendar />} />
        }
      />
      <Route
        path={URLS.changeManagement.changes.list.route}
        element={<PrivateElement element={<SuperAdminApplication.Changes />} />}
      />
      <Route
        path={URLS.changeManagement.requests.list.route}
        element={
          <PrivateElement element={<SuperAdminApplication.ChangeRequests />} />
        }
      />
      <Route
        path={URLS.publicUI.changeManagement.requests.detail.route}
        element={<SuperAdminApplication.ChangeRequestProxyRedirect />}
      />
      <Route
        path={URLS.changeManagement.requests.preview.route}
        element={<SuperAdminApplication.ChangeRequestPreview />}
      />

      {/* Orders Management */}
      <Route
        path={URLS.orderManagement.orders.list.route}
        element={<PrivateElement element={<SuperAdminApplication.Orders />} />}
      />
      <Route
        path={URLS.orderManagement.workflows.index.route}
        element={
          <PrivateElement element={<SuperAdminApplication.WorkPlans />} />
        }
      />

      {/* Reports */}
      <Route
        path={URLS.reports.invoices.route}
        element={
          <PrivateElement element={<SuperAdminApplication.InvoicesReport />} />
        }
      />
      <Route
        path={URLS.reports.ordersPDDStatus.route}
        element={
          <PrivateElement
            element={<SuperAdminApplication.OrdersPDDStatusReport />}
          />
        }
      />
      <Route
        path={URLS.reports.circuitsStartDateDelta.route}
        element={
          <PrivateElement
            element={<SuperAdminApplication.CircuitsStartDateDeltaReport />}
          />
        }
      />
      <Route
        path={URLS.reports.deviceFirmware.route}
        element={
          <PrivateElement
            element={<SuperAdminApplication.DeviceFirmwareReport />}
          />
        }
      />
      <Route
        path={URLS.reports.devicesPerManagedService.route}
        element={
          <PrivateElement
            element={<SuperAdminApplication.ManagedServiceDevicesReport />}
          />
        }
      />
      <Route
        path={URLS.reports.siteDeploymentStatus.route}
        element={
          <PrivateElement
            element={<SuperAdminApplication.SiteDeploymentStatusReport />}
          />
        }
      />

      {/* NetBRM billing integration */}
      <Route
        path={URLS.admin.netbrm.overview.index.route}
        element={
          <PrivateElement element={<SuperAdminApplication.NetBRMOverview />} />
        }
      />
      <Route
        path={URLS.admin.netbrm.mappings.list.route}
        element={
          <PrivateElement element={<SuperAdminApplication.NetBRMMappings />} />
        }
      />
      <Route
        path={URLS.admin.netbrm.commands.list.route}
        element={
          <PrivateElement element={<SuperAdminApplication.NetBRMSyncQueue />} />
        }
      />
      <Route
        path={URLS.admin.netbrm.links.list.route}
        element={
          <PrivateElement element={<SuperAdminApplication.NetBRMLinks />} />
        }
      />
      <Route
        path={URLS.admin.netbrm.accountLinks.list.route}
        element={
          <PrivateElement
            element={<SuperAdminApplication.NetBRMAccountOverrides />}
          />
        }
      />
      <Route
        path={URLS.admin.netbrm.restrictionDecisions.list.route}
        element={
          <PrivateElement
            element={<SuperAdminApplication.NetBRMRestrictionDecisions />}
          />
        }
      />
      <Route
        path={URLS.admin.netbrm.events.list.route}
        element={
          <PrivateElement
            element={<SuperAdminApplication.NetBRMWebhookEvents />}
          />
        }
      />
      <Route
        path={URLS.admin.netbrm.inboundOrders.list.route}
        element={
          <PrivateElement
            element={<SuperAdminApplication.NetBRMInboundOrders />}
          />
        }
      />
      <Route
        path={URLS.admin.netbrm.companyBilling.list.route}
        element={
          <PrivateElement
            element={<SuperAdminApplication.NetBRMCompanyBilling />}
          />
        }
      />

      {/* Voice Billing */}
      <Route
        path={URLS.voiceBilling.SIPTrunks.list.route}
        element={
          <PrivateElement element={<SuperAdminApplication.SIPTrunks />} />
        }
      />
      <Route
        path={URLS.voiceBilling.phoneNumberRanges.list.route}
        element={
          <PrivateElement
            element={<SuperAdminApplication.PhoneNumberRanges />}
          />
        }
      />
      <Route
        path={URLS.voiceBilling.answerPoints.list.route}
        element={
          <PrivateElement element={<SuperAdminApplication.AnswerPoints />} />
        }
      />

      {/* Forms Constructor */}
      <Route
        path={URLS.admin.formBuilder.templates.list.route}
        element={
          <PrivateElement
            element={<SuperAdminApplication.FormBuilderTemplates />}
          />
        }
      />
      <Route
        path={URLS.admin.formBuilder.forms.list.route}
        element={
          <PrivateElement
            element={<SuperAdminApplication.FormBuilderForms />}
          />
        }
      />

      {/* Operations */}
      <Route
        path={URLS.admin.operations.circuitRelocation.index.route}
        element={
          <PrivateElement
            element={<SuperAdminApplication.CircuitRelocation />}
          />
        }
      />
      <Route
        path={URLS.admin.operations.tagManagement.index.route}
        element={
          <PrivateElement element={<SuperAdminApplication.TagManagement />} />
        }
      />

      {/* Vendor Management */}
      <Route
        path={URLS.admin.vendorManagement.carriers.list.route}
        element={
          <PrivateElement element={<SuperAdminApplication.Carriers />} />
        }
      />
      <Route
        path={URLS.admin.vendorManagement.maintenanceOrganizations.list.route}
        element={
          <PrivateElement
            element={<SuperAdminApplication.MaintenanceOrganizations />}
          />
        }
      />

      {/* Circuits and Devices */}
      <Route
        path={URLS.admin.circuitsAndDevices.bandwidths.list.route}
        element={
          <PrivateElement element={<SuperAdminApplication.Bandwidths />} />
        }
      />
      <Route
        path={URLS.admin.circuitsAndDevices.circuitTypes.list.route}
        element={
          <PrivateElement element={<SuperAdminApplication.CircuitTypes />} />
        }
      />
      <Route
        path={URLS.admin.circuitsAndDevices.deviceManufacturers.list.route}
        element={
          <PrivateElement
            element={<SuperAdminApplication.DeviceManufacturers />}
          />
        }
      />
      <Route
        path={URLS.admin.circuitsAndDevices.deviceTags.list.route}
        element={
          <PrivateElement element={<SuperAdminApplication.DeviceTags />} />
        }
      />
      <Route
        path={URLS.admin.circuitsAndDevices.interfaceTypes.list.route}
        element={
          <PrivateElement element={<SuperAdminApplication.InterfaceTypes />} />
        }
      />
      <Route
        path={URLS.admin.circuitsAndDevices.interfaceConfigurations.list.route}
        element={
          <PrivateElement
            element={<SuperAdminApplication.InterfaceConfigurations />}
          />
        }
      />
      <Route
        path={URLS.admin.circuitsAndDevices.deviceCategories.list.route}
        element={
          <PrivateElement
            element={<SuperAdminApplication.DeviceCategories />}
          />
        }
      />

      {/* Provisioning */}
      <Route
        path={URLS.admin.provisioning.customerStatuses.list.route}
        element={
          <PrivateElement
            element={<SuperAdminApplication.CustomerProvisioningStatuses />}
          />
        }
      />

      {/* Service Messages */}
      <Route
        path={URLS.admin.serviceMessages.list.route}
        element={
          <PrivateElement element={<SuperAdminApplication.ServiceMessages />} />
        }
      />

      {/* Email Settings */}
      <Route
        path={URLS.admin.emailSettings.emailSettings.list.route}
        element={
          <PrivateElement element={<SuperAdminApplication.EmailSettings />} />
        }
      />
      <Route
        path={URLS.admin.emailSettings.emails.list.route}
        element={<PrivateElement element={<SuperAdminApplication.Emails />} />}
      />

      {/* Users and Groups */}
      <Route
        path={URLS.admin.userAndGroups.users.list.route}
        element={<PrivateElement element={<SuperAdminApplication.Users />} />}
      />
      <Route
        path={URLS.admin.userAndGroups.permissions.list.route}
        element={
          <PrivateElement element={<SuperAdminApplication.Permissions />} />
        }
      />
      <Route
        path={URLS.admin.userAndGroups.permissionsSets.list.route}
        element={
          <PrivateElement element={<SuperAdminApplication.PermissionsSets />} />
        }
      />
      <Route
        path={URLS.admin.userAndGroups.userGroups.list.route}
        element={
          <PrivateElement element={<SuperAdminApplication.UserGroups />} />
        }
      />

      {/* Product Management */}
      <Route
        path={URLS.admin.productManagement.serviceCategories.list.route}
        element={
          <PrivateElement
            element={<SuperAdminApplication.ServiceCategories />}
          />
        }
      />
      <Route
        path={URLS.admin.productManagement.serviceTypes.list.route}
        element={
          <PrivateElement element={<SuperAdminApplication.ServiceTypes />} />
        }
      />
      <Route
        path={URLS.admin.productManagement.mobilePlans.list.route}
        element={
          <PrivateElement element={<SuperAdminApplication.RatePlans />} />
        }
      />
      <Route
        path={URLS.admin.productManagement.mobileAddons.list.route}
        element={
          <PrivateElement element={<SuperAdminApplication.MobileAddOns />} />
        }
      />
      <Route
        path={URLS.admin.productManagement.handoverDocumentTemplates.list.route}
        element={
          <PrivateElement
            element={<SuperAdminApplication.HandoverDocumentTemplates />}
          />
        }
      />
      <Route
        path={URLS.admin.productManagement.workPlanTemplates.list.route}
        element={
          <PrivateElement
            element={<SuperAdminApplication.WorkPlanTemplates />}
          />
        }
      />

      {/* ConnectWise */}
      <Route
        path={URLS.admin.connectwise.syncEvents.list.route}
        element={
          <PrivateElement
            element={<SuperAdminApplication.ConnectWiseSyncEvents />}
          />
        }
      />

      {/* Voice Billing */}
      <Route
        path={URLS.admin.voiceBilling.voiceCodecs.list.route}
        element={
          <PrivateElement element={<SuperAdminApplication.VoiceCodecs />} />
        }
      />

      <Route
        path={URLS.userInvitations.acceptInvitation.route}
        element={<SuperAdminApplication.AcceptUserInvitation />}
      />

      <Route path="*" element={<PrivateElement element={<PageNotFound />} />} />
    </Routes>
  </Suspense>
);

export const customerRoutes = (
  <Suspense fallback={<LoadingComponent />}>
    <Routes>
      <Route
        path={URLS.auth.login.route}
        element={<Navigate to={URLS.dashboard.index.buildPath()} replace />}
      />

      <Route
        path={URLS.auth.authCallback.route}
        element={<PrivateElement element={<AuthCallback />} />}
      />

      <Route
        path={URLS.account.logout.route}
        element={<PrivateElement element={<LogoutPage />} />}
      />
      <Route
        path={URLS.customerUI.companyProfile.index.route}
        element={
          <PrivateElement element={<CustomerApplication.CompanyProfile />} />
        }
      />

      <Route
        path={URLS.customerUI.dashboard.index.route}
        element={<PrivateElement element={<CustomerApplication.Dashboard />} />}
      />

      <Route
        path={URLS.customerUI.serviceDelivery.index.route}
        element={
          <PrivateElement element={<CustomerApplication.OrderDeliveryPage />} />
        }
      />
      <Route
        path={URLS.customerUI.serviceDelivery.detail.route}
        element={
          <PrivateElement
            element={<CustomerApplication.OrderDeliveryDetailsPage />}
          />
        }
      />

      <Route
        path={URLS.customerUI.services.index.route}
        element={
          <PrivateElement
            element={<CustomerApplication.ActiveServicesListPage />}
          />
        }
      />
      <Route
        path={URLS.customerUI.services.detail.route}
        element={
          <PrivateElement
            element={<CustomerApplication.ActiveServiceDetailsPage />}
          />
        }
      />
      <Route
        path={URLS.customerUI.mobileVoice.index.route}
        element={
          <PrivateElement
            element={<CustomerApplication.TelcoInaBoxSimCardsListPage />}
          />
        }
      />
      <Route
        path={URLS.customerUI.mobileVoice.detail.route}
        element={
          <PrivateElement
            element={<CustomerApplication.TelcoInaBoxSimCardDetails />}
          />
        }
      />
      <Route
        path={URLS.customerUI.netsgMobile.services.index.route}
        element={
          <PrivateElement
            element={<CustomerApplication.CustomerNetSGMobileServicesList />}
          />
        }
      />
      <Route
        path={URLS.customerUI.netsgMobile.services.detail.route}
        element={
          <PrivateElement
            element={<CustomerApplication.CustomerNetSGMobileServiceDetail />}
          />
        }
      />
      <Route
        path={URLS.customerUI.netsgMobile.pools.index.route}
        element={
          <PrivateElement
            element={<CustomerApplication.CustomerNetSGMobilePoolsList />}
          />
        }
      />
      <Route
        path={URLS.customerUI.netsgMobile.pools.detail.route}
        element={
          <PrivateElement
            element={<CustomerApplication.CustomerNetSGMobilePoolDetail />}
          />
        }
      />

      <Route
        path={URLS.customerUI.managedServices.index.route}
        element={
          <PrivateElement
            element={<CustomerApplication.ManagedServicesListPage />}
          />
        }
      />
      <Route
        path={URLS.customerUI.managedServices.detail.route}
        element={
          <PrivateElement
            element={<CustomerApplication.ManagedServiceDetailsPage />}
          />
        }
      />
      <Route
        path={URLS.customerUI.managedServices.devices.detail.route}
        element={
          <PrivateElement
            element={<CustomerApplication.ManagedDeviceDetailsPage />}
          />
        }
      />

      <Route
        path={URLS.customerUI.changeManagement.changes.index.route}
        element={
          <PrivateElement element={<CustomerApplication.ChangesListPage />} />
        }
      />
      <Route
        path={URLS.customerUI.changeManagement.changes.detail.route}
        element={
          <PrivateElement element={<CustomerApplication.ChangeDetailsPage />} />
        }
      />

      <Route
        path={URLS.customerUI.changeManagement.requests.index.route}
        element={
          <PrivateElement
            element={<CustomerApplication.ChangeRequestsListPage />}
          />
        }
      />
      <Route
        path={URLS.customerUI.changeManagement.requests.detail.route}
        element={
          <PrivateElement
            element={<CustomerApplication.ChangeRequestDetailsPage />}
          />
        }
      />
      <Route
        path={URLS.publicUI.changeManagement.requests.detail.route}
        element={<CustomerApplication.ChangeRequestProxyRedirect />}
      />

      <Route
        path={URLS.customerUI.support.index.route}
        element={
          <PrivateElement
            element={<CustomerApplication.SupportTicketsPage />}
          />
        }
      />
      <Route
        path={URLS.customerUI.support.openTicket.route}
        element={
          <PrivateElement element={<CustomerApplication.OpenTicketPage />} />
        }
      />
      <Route
        path={URLS.customerUI.support.detail.route}
        element={
          <PrivateElement element={<CustomerApplication.TicketDetailsPage />} />
        }
      />

      <Route
        path={URLS.customerUI.monitoring.myNetwork.index.route}
        element={<PrivateElement element={<CustomerApplication.MyNetwork />} />}
      />

      <Route
        path={URLS.customerUI.reports.index.route}
        element={
          <PrivateElement element={<CustomerApplication.ReportsListPage />} />
        }
      />
      <Route
        path={URLS.customerUI.reports.services.route}
        element={
          <PrivateElement element={<CustomerApplication.ServicesReport />} />
        }
      />
      <Route
        path={URLS.customerUI.reports.supportTickets.route}
        element={
          <PrivateElement
            element={<CustomerApplication.SupportTicketsReport />}
          />
        }
      />
      <Route
        path={URLS.customerUI.reports.changeManagement.route}
        element={
          <PrivateElement
            element={<CustomerApplication.ChangeManagementReport />}
          />
        }
      />
      <Route
        path={URLS.customerUI.reports.managedDevices.route}
        element={
          <PrivateElement
            element={<CustomerApplication.ManagedDevicesReport />}
          />
        }
      />
      <Route
        path={URLS.customerUI.reports.managedServicesDeviceAvailability.route}
        element={
          <PrivateElement
            element={
              <CustomerApplication.ManagedServicesDeviceAvailabilityReport />
            }
          />
        }
      />
      <Route
        path={URLS.customerUI.reports.serviceAvailability.route}
        element={
          <PrivateElement
            element={<CustomerApplication.ServiceAvailabilityReport />}
          />
        }
      />
      <Route
        path={URLS.customerUI.reports.alertHistory.route}
        element={
          <PrivateElement
            element={<CustomerApplication.AlertHistoryReport />}
          />
        }
      />
      <Route
        path={URLS.customerUI.reports.circuitUtilizationByLocation.route}
        element={
          <PrivateElement
            element={<CustomerApplication.CircuitUtilizationByLocationReport />}
          />
        }
      />
      <Route
        path={URLS.customerUI.reports.siteDeploymentStatus.route}
        element={
          <PrivateElement
            element={<CustomerApplication.SiteDeploymentStatusReport />}
          />
        }
      />

      <Route
        path={URLS.customerUI.forms.detail.route}
        element={
          <PrivateElement element={<PublicApplication.ConstructedFormPage />} />
        }
      />

      <Route
        path={URLS.customerUI.userInvitations.acceptInvitation.route}
        element={<CustomerApplication.AcceptUserInvitation />}
      />

      <Route path="*" element={<PrivateElement element={<PageNotFound />} />} />
    </Routes>
  </Suspense>
);

export const noAccessRoutes = (
  <Suspense fallback={<LoadingComponent />}>
    <Routes>
      <Route
        path={URLS.auth.login.route}
        element={<Navigate to={URLS.dashboard.index.buildPath()} replace />}
      />
      <Route
        path={URLS.account.logout.route}
        element={<PrivateElement element={<LogoutPage />} />}
      />
      <Route
        path={URLS.dashboard.index.route}
        element={<PrivateElement element={<AccountNotSetYetPage />} />}
      />
      <Route path="*" element={<PrivateElement element={<PageNotFound />} />} />
    </Routes>
  </Suspense>
);

export const publicRoutes = (
  <Suspense fallback={<LoadingComponent />}>
    <Routes>
      <Route
        path={URLS.dashboard.index.route}
        element={<Navigate to={URLS.auth.login.buildPath()} replace />}
      />
      <Route path={URLS.auth.login.route} element={<LoginPage />} />
      <Route
        path={URLS.publicUI.forms.detail.route}
        element={<PublicApplication.ConstructedFormPage />}
      />
      <Route
        path={URLS.publicUI.changeManagement.requests.detail.route}
        element={<PublicApplication.ChangeRequestApproval />}
      />
      <Route
        path={URLS.publicUI.userInvitations.acceptInvitation.route}
        element={<PublicApplication.AcceptUserInvitation />}
      />
      <Route
        path={URLS.account.logout.route}
        element={<Navigate to={URLS.auth.login.buildPath()} replace />}
      />
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  </Suspense>
);
